import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Plus, MessageCircle, ChevronRight, Loader, RefreshCw, ArrowUp, Bookmark, Bell } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LIFECYCLE_META = {
  trade_idea: { label: 'Trade Idea', color: '#6366f1' },
  doubt:      { label: 'Doubt',      color: '#f59e0b' },
  backtest:   { label: 'Backtest',   color: '#8b5cf6' },
  profit:     { label: 'Profit',     color: '#16c784' },
  loss:       { label: 'Loss',       color: '#ef4444' },
  mistake:    { label: 'Mistake',    color: '#f97316' },
};

const FILTERS = [
  { key: null,          label: 'All' },
  { key: 'trade_idea',  label: 'Trade Idea' },
  { key: 'doubt',       label: 'Doubt' },
  { key: 'backtest',    label: 'Backtest' },
  { key: 'profit',      label: 'Profit' },
  { key: 'loss',        label: 'Loss' },
  { key: 'mistake',     label: 'Mistake' },
];

const TABS = [
  { key: 'all',   label: 'All Posts' },
  { key: 'mine',  label: 'My Posts' },
  { key: 'saved', label: 'Saved' },
];

function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function AttachPreview({ attachments }) {
  if (!attachments?.length) return null;
  const chips = [];
  for (const a of attachments) {
    const s = a.snapshot || {};
    if (a.attach_type === 'backtest' && s.total_return_pct != null)
      chips.push(`↑ ${parseFloat(s.total_return_pct).toFixed(1)}% return`);
    if (a.attach_type === 'pnl_summary' && s.total_pnl != null)
      chips.push(`P&L ₹${parseInt(s.total_pnl).toLocaleString('en-IN')}`);
    if (a.attach_type === 'discipline' && s.score != null)
      chips.push(`${s.score} discipline`);
    if (a.attach_type === 'trade_history')
      chips.push('Trade data');
    if (a.attach_type === 'decision_engine')
      chips.push(s.grade ? `${s.grade} Grade — ${s.instrument?.label || 'DE Session'}` : 'DE Session');
    if (a.attach_type === 'trade_analysis' && s.symbol)
      chips.push(`${s.symbol}${s.pnl != null ? ` ${parseFloat(s.pnl) >= 0 ? '+' : ''}₹${Math.abs(Math.round(parseFloat(s.pnl))).toLocaleString('en-IN')}` : ''}`);
    if (a.attach_type === 'trade_story' && s.trade_setup?.symbol) {
      const pnl = s.actual_results?.pnl;
      chips.push(`${s.trade_setup.symbol} Trade Story${pnl != null ? ' — ' + (parseFloat(pnl) >= 0 ? '+' : '') + '₹' + Math.abs(Math.round(parseFloat(pnl))).toLocaleString('en-IN') : ''}`);
    }
    if (a.attach_type === 'capital_tier' && s.tier)
      chips.push(s.tier + ' tier');
  }
  if (!chips.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
      {chips.map((c, i) => (
        <span key={i} style={{
          fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
          background: 'rgba(99,102,241,0.12)', color: '#a5b4fc',
          border: '1px solid rgba(99,102,241,0.25)',
        }}>
          {c}
        </span>
      ))}
    </div>
  );
}

function PostCard({ post, onClick }) {
  const [hovered, setHovered] = useState(false);
  const [voted, setVoted] = useState(post.my_reaction || false);
  const [saved, setSaved] = useState(post.my_save || false);
  const [saving, setSaving] = useState(false);
  const lm = LIFECYCLE_META[post.lifecycle] || LIFECYCLE_META.trade_idea;
  const score = (post.reaction_count || 0) + (voted ? 1 : 0);

  const handleSave = async (e) => {
    e.stopPropagation();
    if (saving) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/forum/posts/${post.id}/save`);
      setSaved(res.data.saved);
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', gap: 0,
        background: hovered ? 'rgba(255,255,255,0.03)' : 'transparent',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        transition: 'background 0.12s ease',
        cursor: 'pointer',
      }}
    >
      {/* Vote column */}
      <div
        style={{
          width: 44, flexShrink: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          paddingTop: 12, gap: 2,
          background: hovered ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.01)',
          borderRight: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <button
          onClick={e => { e.stopPropagation(); setVoted(v => !v); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '4px', borderRadius: 4,
            color: voted ? lm.color : 'rgba(255,255,255,0.3)',
            transition: 'color 0.12s, transform 0.12s',
            transform: voted ? 'scale(1.1)' : 'scale(1)',
          }}
        >
          <ArrowUp size={16} strokeWidth={voted ? 2.5 : 1.8} />
        </button>
        <span style={{
          fontSize: 12, fontWeight: 800,
          color: voted ? lm.color : 'rgba(255,255,255,0.55)',
          lineHeight: 1, minWidth: 20, textAlign: 'center',
          transition: 'color 0.12s',
        }}>
          {score}
        </span>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0, padding: '12px 12px 10px 12px' }} onClick={onClick}>

        {/* Meta: posted by · time */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6, flexWrap: 'wrap' }}>
          {/* Lifecycle flair */}
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '1px 8px', borderRadius: 4,
            background: lm.color + '25', color: lm.color,
            border: `1px solid ${lm.color}40`,
            letterSpacing: '0.2px',
          }}>
            {lm.label}
          </span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.28)' }}>·</span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>
            Posted by <span style={{ color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>{post.author_name}</span>
          </span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.28)' }}>·</span>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{timeAgo(post.created_at)}</span>
        </div>

        {/* Title — hero */}
        <p style={{
          fontSize: 15, fontWeight: 700,
          color: hovered ? '#ffffff' : '#e2e5f0',
          lineHeight: 1.4, marginBottom: 6,
          transition: 'color 0.12s ease',
        }}>
          {post.title}
        </p>

        {/* Description */}
        {post.description && post.description !== post.title && (
          <p style={{
            fontSize: 12, color: 'rgba(255,255,255,0.48)',
            lineHeight: 1.6, marginBottom: 8,
          }}>
            {post.description.length > 160 ? post.description.substring(0, 160) + '…' : post.description}
          </p>
        )}

        {/* Instruments + tags */}
        {(post.instruments?.length > 0 || post.tags?.length > 0) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
            {(post.instruments || []).slice(0, 3).map((ins, i) => (
              <span key={i} style={{
                fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                background: 'rgba(99,102,241,0.14)', color: '#a5b4fc',
                border: '1px solid rgba(99,102,241,0.28)',
              }}>
                {ins}
              </span>
            ))}
            {(post.tags || []).slice(0, 3).map((tag, i) => (
              <span key={i} style={{ fontSize: 11, color: 'rgba(255,255,255,0.32)' }}>
                #{tag}
              </span>
            ))}
          </div>
        )}

        {/* Attachment chips */}
        <AttachPreview attachments={post.attachments} />

        {/* Action bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 10, flexWrap: 'wrap' }}>
          <button
            onClick={onClick}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 9px', borderRadius: 6, border: 'none',
              background: 'transparent',
              color: 'rgba(255,255,255,0.38)', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', transition: 'background 0.1s, color 0.1s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.38)'; }}
          >
            <MessageCircle size={13} strokeWidth={1.8} />
            {post.comment_count || 0} Comments
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 9px', borderRadius: 6, border: 'none',
              background: saved ? 'rgba(245,158,11,0.10)' : 'transparent',
              color: saved ? '#f59e0b' : 'rgba(255,255,255,0.38)',
              fontSize: 12, fontWeight: 600,
              cursor: saving ? 'default' : 'pointer',
              transition: 'background 0.1s, color 0.1s',
            }}
            onMouseEnter={e => { if (!saved) { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; } }}
            onMouseLeave={e => { if (!saved) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.38)'; } }}
          >
            <Bookmark size={13} strokeWidth={1.8} fill={saved ? '#f59e0b' : 'none'} />
            {saved ? 'Saved' : 'Save'}
          </button>

          <button
            onClick={onClick}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 9px', borderRadius: 6, border: 'none',
              background: 'transparent',
              color: 'rgba(255,255,255,0.38)', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', transition: 'background 0.1s, color 0.1s',
              marginLeft: 'auto',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = lm.color; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.38)'; }}
          >
            View post <ChevronRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}

function NotificationPanel({ notifications, onClose, onMarkRead }) {
  const panelRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      ref={panelRef}
      style={{
        position: 'absolute', top: 44, right: 0, zIndex: 200,
        width: 320, maxWidth: 'calc(100vw - 32px)', maxHeight: 400, overflowY: 'auto',
        background: '#1a1d2e', border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      }}
    >
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#eceef5' }}>Notifications</span>
        <button
          onClick={onMarkRead}
          style={{
            fontSize: 11, color: '#f59e0b', background: 'none', border: 'none',
            cursor: 'pointer', fontWeight: 600,
          }}
        >
          Mark all read
        </button>
      </div>
      {notifications.length === 0 ? (
        <div style={{ padding: '24px 16px', textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
          No notifications yet
        </div>
      ) : (
        notifications.map(n => (
          <div key={n.id} style={{
            padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)',
            background: n.read ? 'transparent' : 'rgba(245,158,11,0.05)',
          }}>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, margin: 0 }}>
              <span style={{ fontWeight: 700, color: '#eceef5' }}>{n.from_user_name}</span>
              {' commented on '}
              <span style={{ color: '#a5b4fc' }}>"{n.post_title?.length > 40 ? n.post_title.substring(0, 40) + '…' : n.post_title}"</span>
            </p>
            <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.28)', margin: '4px 0 0' }}>
              {timeAgo(n.created_at)}
              {!n.read && <span style={{ marginLeft: 6, color: '#f59e0b', fontWeight: 700 }}>• new</span>}
            </p>
          </div>
        ))
      )}
    </div>
  );
}

export default function ForumPage() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('all');
  const [lifecycleFilter, setLifecycleFilter] = useState(null);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const effectiveFilter = tab === 'all' ? lifecycleFilter : tab;

  const [notifCount, setNotifCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/forum/notifications`);
      setNotifCount(res.data.unread_count || 0);
      setNotifications(res.data.notifications || []);
    } catch {}
  }, []);

  const handleMarkRead = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/forum/notifications/mark-read`);
      setNotifCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  }, []);

  const fetchPosts = useCallback(async (lc, cur, append = false, bust = false) => {
    try {
      append ? setLoadingMore(true) : setLoading(true);
      let newPosts, next_cursor;

      if (lc === 'saved') {
        const params = { limit: 20 };
        if (cur) params.cursor = cur;
        const res = await axios.get(`${API_URL}/forum/saved`, { params });
        newPosts = res.data.posts;
        next_cursor = res.data.next_cursor;
      } else {
        const params = { limit: 20 };
        if (lc && lc !== 'mine') params.lifecycle = lc;
        if (lc === 'mine') params.mine = true;
        if (cur) params.cursor = cur;
        if (bust) params.refresh = true;
        const res = await axios.get(`${API_URL}/forum/feed`, { params });
        newPosts = res.data.posts;
        next_cursor = res.data.next_cursor;
      }

      setPosts(prev => append ? [...prev, ...newPosts] : newPosts);
      setCursor(next_cursor);
      setHasMore(newPosts.length === 20);
    } catch {}
    finally { append ? setLoadingMore(false) : setLoading(false); }
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchPosts(effectiveFilter, null, false, true);
    setRefreshing(false);
  }, [effectiveFilter, fetchPosts]);

  useEffect(() => { setCursor(null); setHasMore(true); fetchPosts(effectiveFilter, null); }, [tab, lifecycleFilter]); // eslint-disable-line
  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleTabChange = (key) => { setTab(key); setCursor(null); setHasMore(true); };
  const handleFilterChange = (key) => { setLifecycleFilter(key); setCursor(null); setHasMore(true); };

  const handleBellClick = () => {
    setNotifOpen(o => !o);
    if (!notifOpen && notifCount > 0) handleMarkRead();
  };

  return (
    <div style={{ backgroundColor: colors.background, minHeight: '100vh', paddingBottom: 32 }}>
      {/* Header */}
      <div style={{ padding: '20px 16px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div>
            <p style={{ fontSize: 11, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#f59e0b', marginBottom: 2 }}>Community</p>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: '#eceef5', letterSpacing: '-0.02em' }}>Strategy Feed</h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Notification bell */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={handleBellClick}
                title="Notifications"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 36, height: 36, borderRadius: 10,
                  background: notifOpen ? 'rgba(245,158,11,0.12)' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${notifOpen ? 'rgba(245,158,11,0.35)' : 'rgba(255,255,255,0.1)'}`,
                  cursor: 'pointer', position: 'relative',
                }}
              >
                <Bell size={14} color={notifOpen ? '#f59e0b' : 'rgba(255,255,255,0.5)'} />
                {notifCount > 0 && (
                  <span style={{
                    position: 'absolute', top: -4, right: -4,
                    minWidth: 16, height: 16, borderRadius: 8,
                    background: '#ef4444', color: '#fff',
                    fontSize: 9, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 3px',
                  }}>
                    {notifCount > 99 ? '99+' : notifCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <NotificationPanel
                  notifications={notifications}
                  onClose={() => setNotifOpen(false)}
                  onMarkRead={handleMarkRead}
                />
              )}
            </div>

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              title="Refresh feed"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 36, height: 36, borderRadius: 10,
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                cursor: refreshing ? 'not-allowed' : 'pointer',
              }}
            >
              <RefreshCw size={14} color="rgba(255,255,255,0.5)"
                style={{ animation: refreshing ? 'spin 0.8s linear infinite' : 'none' }} />
            </button>
            <button
              onClick={() => navigate('/forum/create')}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px',
                borderRadius: 12, background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                border: 'none', cursor: 'pointer',
              }}
            >
              <Plus size={14} color="#fff" />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>Share</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tab bar: All Posts | My Posts | Saved */}
      <div style={{ padding: '14px 16px 0', display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        {TABS.map(t => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => handleTabChange(t.key)}
              style={{
                padding: '7px 14px', fontSize: 12, fontWeight: 700,
                background: 'none', border: 'none', cursor: 'pointer',
                color: active ? '#f59e0b' : 'rgba(255,255,255,0.38)',
                borderBottom: active ? '2px solid #f59e0b' : '2px solid transparent',
                marginBottom: -1,
                transition: 'color 0.15s, border-color 0.15s',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Lifecycle filter chips — only on All Posts tab */}
      {tab === 'all' && (
        <div style={{ padding: '10px 16px 0', display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {FILTERS.map(f => {
            const active = lifecycleFilter === f.key;
            const lm = f.key ? LIFECYCLE_META[f.key] : null;
            const ac = lm?.color || '#f59e0b';
            return (
              <button
                key={String(f.key)}
                onClick={() => handleFilterChange(f.key)}
                style={{
                  whiteSpace: 'nowrap', padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                  background: active ? ac + '22' : 'rgba(255,255,255,0.05)',
                  color: active ? ac : 'rgba(255,255,255,0.45)',
                  border: `1px solid ${active ? ac + '50' : 'rgba(255,255,255,0.1)'}`,
                  cursor: 'pointer', flexShrink: 0,
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Posts */}
      <div style={{ padding: '14px 16px 0' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Loader size={24} className="animate-spin" style={{ color: '#f59e0b', margin: '0 auto' }} />
          </div>
        ) : posts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>
            {tab === 'saved' ? 'No saved posts yet. Hit Save on any post to bookmark it.' :
             tab === 'mine'  ? 'You haven\'t posted anything yet.' :
             'No strategies yet. Be the first to share!'}
          </div>
        ) : (
          <div style={{
            borderRadius: 16, overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.09)',
            background: 'rgba(255,255,255,0.015)',
          }}>
            {posts.map(post => (
              <PostCard key={post.id} post={post} onClick={() => navigate(`/forum/${post.id}`)} />
            ))}
            {hasMore && (
              <button
                onClick={() => fetchPosts(effectiveFilter, cursor, true)}
                disabled={loadingMore}
                style={{
                  width: '100%', padding: '13px', textAlign: 'center', fontSize: 12,
                  color: '#f59e0b', background: 'transparent',
                  border: 'none', borderTop: '1px solid rgba(255,255,255,0.07)',
                  cursor: 'pointer', fontWeight: 600,
                }}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
